import React, { useState } from 'react';
import { 
    FaTimes, FaCopy, FaCheck, FaShareAlt, FaWhatsapp, 
    FaFacebookF, FaLinkedinIn, FaEnvelope, FaExternalLinkAlt 
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import { getFullImageUrl } from '../../utils/url';
import { buildShareContent } from '../../utils/brandShareHelper';

export default function AdminShareModal({
    isOpen,
    onClose,
    title = 'Listing on StudentsUnion',
    category = 'Listing',
    url = '',
    price = '',
    location = '',
    image = '',
    description = ''
}) {
    const [copied, setCopied] = useState(false);
    const [copiedMsg, setCopiedMsg] = useState(false);

    if (!isOpen) return null;

    const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

    // Resolve brand specific metadata and formatted copy
    const { brand, previewUrl, whatsappText, plainText, emailSubject } = buildShareContent({
        url: shareUrl,
        title,
        category,
        price,
        location,
        description
    });

    const effectiveShareUrl = previewUrl || shareUrl;
    const fullWhatsappMessage = `${whatsappText}\n${effectiveShareUrl}`;
    const fullPlainMessage = `${plainText}\n${effectiveShareUrl}`;

    const handleCopy = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareUrl);
            } else {
                const ta = document.createElement('textarea');
                ta.value = shareUrl;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopied(true);
            toast.success('Public link copied to clipboard!');
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            toast.error('Failed to copy link.');
        }
    };

    const handleCopyFullMessage = async () => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(fullPlainMessage);
            } else {
                const ta = document.createElement('textarea');
                ta.value = fullPlainMessage;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopiedMsg(true);
            toast.success(`Full ${brand.brandName} message copied for WhatsApp!`);
            setTimeout(() => setCopiedMsg(false), 2500);
        } catch (e) {
            toast.error('Failed to copy message.');
        }
    };

    const shareChannels = [
        {
            name: 'WhatsApp',
            icon: <FaWhatsapp size={18} />,
            bg: '#25D366',
            color: '#fff',
            href: `https://api.whatsapp.com/send?text=${encodeURIComponent(fullWhatsappMessage)}`
        },
        {
            name: 'Facebook',
            icon: <FaFacebookF size={16} />,
            bg: '#1877F2',
            color: '#fff',
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
        },
        {
            name: 'X (Twitter)',
            icon: <FaXTwitter size={16} />,
            bg: '#000000',
            color: '#fff',
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${brand.brandName}: ${title}`)}&url=${encodeURIComponent(shareUrl)}`
        },
        {
            name: 'LinkedIn',
            icon: <FaLinkedinIn size={16} />,
            bg: '#0A66C2',
            color: '#fff',
            href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
        },
        {
            name: 'Email',
            icon: <FaEnvelope size={16} />,
            bg: '#64748B',
            color: '#fff',
            href: `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(fullPlainMessage)}`
        }
    ];

    const displayImage = image ? getFullImageUrl(image) : null;

    return (
        <div 
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '16px'
            }}
            onClick={onClose}
        >
            <div 
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '24px',
                    width: '100%',
                    maxWidth: '500px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    border: '1px solid #f1f5f9',
                    overflow: 'hidden',
                    animation: 'adminModalPop 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header with Brand Logo & Identity */}
                <div style={{
                    padding: '18px 22px',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'linear-gradient(to right, #f8fafc, #ffffff)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '12px',
                            background: brand.bgLight || '#fef2f2',
                            border: `1px solid ${brand.borderColor || '#fecaca'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '6px',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                        }}>
                            {brand.logoPath ? (
                                <img 
                                    src={brand.logoPath} 
                                    alt={brand.brandName} 
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            ) : (
                                <FaShareAlt style={{ color: brand.color, fontSize: '18px' }} />
                            )}
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                    fontSize: '10px',
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    color: brand.color,
                                    background: brand.bgLight,
                                    padding: '2px 8px',
                                    borderRadius: '6px'
                                }}>
                                    {brand.brandName}
                                </span>
                                <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                                    Live Listing
                                </span>
                            </div>
                            <h4 style={{ margin: '3px 0 0 0', fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                                Share {category}
                            </h4>
                            <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
                                {brand.tagline}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: '#f1f5f9',
                            color: '#64748b',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e2e8f0'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Listing Preview Card */}
                <div style={{ padding: '20px' }}>
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '14px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        alignItems: 'center',
                        marginBottom: '16px'
                    }}>
                        {displayImage ? (
                            <img 
                                src={displayImage} 
                                alt={title}
                                onError={(e) => { e.target.style.display = 'none'; }}
                                style={{
                                    width: '64px',
                                    height: '64px',
                                    borderRadius: '10px',
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                    border: '1px solid #e2e8f0'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '10px',
                                background: brand.bgLight || '#fef2f2',
                                border: `1px solid ${brand.borderColor || '#fecaca'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px',
                                flexShrink: 0
                            }}>
                                <img 
                                    src={brand.logoPath} 
                                    alt={brand.brandName} 
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <span style={{
                                display: 'inline-block',
                                fontSize: '10px',
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                color: brand.color,
                                background: brand.bgLight,
                                border: `1px solid ${brand.borderColor}`,
                                padding: '2px 8px',
                                borderRadius: '6px',
                                marginBottom: '4px'
                            }}>
                                {brand.brandName} • {category}
                            </span>
                            <h5 style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: 700,
                                color: '#0f172a',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                {title}
                            </h5>
                            <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#64748b', marginTop: '2px', flexWrap: 'wrap' }}>
                                {price && <strong style={{ color: '#059669' }}>{typeof price === 'number' ? `$${price}` : price}</strong>}
                                {location && <span>• 📍 {location}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Social Channels */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '8px' }}>
                            Share Listing Directly
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '10px' }}>
                            {shareChannels.map((c) => (
                                <a
                                    key={c.name}
                                    href={c.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Share on ${c.name}`}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '10px 4px',
                                        borderRadius: '12px',
                                        backgroundColor: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        color: '#334155',
                                        textDecoration: 'none',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = c.bg;
                                        e.currentTarget.style.color = c.color;
                                        e.currentTarget.style.borderColor = c.bg;
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f8fafc';
                                        e.currentTarget.style.color = '#334155';
                                        e.currentTarget.style.borderColor = '#e2e8f0';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div style={{ marginBottom: '4px' }}>{c.icon}</div>
                                    <span style={{ fontSize: '10px', fontWeight: 700 }}>{c.name}</span>
                                </a>
                            ))}
                        </div>

                        {/* WhatsApp Direct 1-Click Banner */}
                        <a
                            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(fullWhatsappMessage)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                width: '100%',
                                padding: '11px',
                                borderRadius: '12px',
                                backgroundColor: '#25D366',
                                color: '#ffffff',
                                fontSize: '12px',
                                fontWeight: 800,
                                textDecoration: 'none',
                                transition: 'all 0.2s',
                                boxShadow: '0 4px 12px rgba(37, 211, 102, 0.25)'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#20ba59'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#25D366'; }}
                        >
                            <FaWhatsapp size={16} /> Share on WhatsApp with {brand.brandName} Details
                        </a>
                    </div>

                    {/* Copy Link Row */}
                    <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '6px' }}>
                            Public Listing URL
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <input 
                                type="text"
                                readOnly
                                value={shareUrl}
                                style={{
                                    flex: 1,
                                    padding: '9px 12px',
                                    fontSize: '12px',
                                    borderRadius: '10px',
                                    border: '1px solid #cbd5e1',
                                    backgroundColor: '#f8fafc',
                                    color: '#334155',
                                    outline: 'none'
                                }}
                                onFocus={(e) => e.target.select()}
                            />
                            <button
                                onClick={handleCopy}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '9px 16px',
                                    backgroundColor: copied ? '#059669' : '#0f172a',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {copied ? <FaCheck /> : <FaCopy />}
                                {copied ? 'Copied URL!' : 'Copy Link'}
                            </button>
                        </div>
                    </div>

                    {/* Copy Full Message Box for Groups */}
                    <div style={{
                        padding: '12px',
                        borderRadius: '12px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        marginBottom: '14px'
                    }}>
                        <div>
                            <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a' }}>
                                Copy Full {brand.brandName} Listing Message
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                                Pre-formatted with emojis & details for WhatsApp groups
                            </div>
                        </div>
                        <button
                            onClick={handleCopyFullMessage}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 14px',
                                backgroundColor: copiedMsg ? '#059669' : '#ffffff',
                                color: copiedMsg ? '#ffffff' : '#0f172a',
                                border: copiedMsg ? '1px solid #059669' : '1px solid #cbd5e1',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {copiedMsg ? <FaCheck /> : <FaCopy />}
                            {copiedMsg ? 'Copied Message!' : 'Copy Full Text'}
                        </button>
                    </div>

                    {/* Open in New Tab Button */}
                    <a
                        href={shareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '10px',
                            borderRadius: '10px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            color: '#2563eb',
                            fontSize: '12px',
                            fontWeight: 700,
                            textDecoration: 'none',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                    >
                        <FaExternalLinkAlt size={12} /> Open Live Page in New Tab
                    </a>
                </div>
            </div>
        </div>
    );
}
